import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { DiscoverEvent, DiscoverFilters } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveTable } from "@/components/responsive-table";
import { Search, MapPin, Calendar, ChevronLeft, ChevronRight, List } from "lucide-react";
import { SchoolCrest } from "@/components/school-crest";
import { useAuth } from "@/lib/auth-context";

const typeBadge: Record<string, string> = {
  match_day: "rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700",
  practice: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700",
  tryout: "rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700",
};

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function DiscoverPage() {
  const navigate = useNavigate();
  // 200 -- the empty-state nudge must not tell a signed-in user to "Sign in".
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<DiscoverEvent[]>([]);
  const [filters, setFilters] = useState<DiscoverFilters>({ sports: [], provinces: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read filters from URL
  const search = searchParams.get("search") ?? "";
  const sport = searchParams.get("sport") ?? "";
  const type = searchParams.get("type") ?? "";
  const province = searchParams.get("province") ?? "";
  const page = Number(searchParams.get("page") ?? "1");
  const perPage = 20;

  const updateParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.delete("page"); // reset page on filter change
      return next;
    });
  }, [setSearchParams]);

  const setPage = useCallback((p: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p > 1) next.set("page", String(p));
      else next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const today = new Date().toISOString().split("T")[0];
    const threeMonths = new Date(Date.now() + 90 * 86400000).toISOString().split("T")[0];

    api.discoverEvents({
      start: today,
      end: threeMonths,
      search: search || undefined,
      sport: sport || undefined,
      type: type || undefined,
      province: province || undefined,
      page,
      per_page: perPage,
    }).then((data) => {
      if (cancelled) return;
      setEvents(data.events);
      setTotal(data.total);
      setFilters(data.filters);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Failed to load events");
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [search, sport, type, province, page]);

  const totalPages = Math.ceil(total / perPage);

  // 102: view-mode driven by URL `?view` so links land on the chosen view,
  // browser Back works through view changes, and there's no localStorage /
  // cross-tab inconsistency. Default = list when param absent.
  const viewMode: "list" | "calendar" = searchParams.get("view") === "calendar" ? "calendar" : "list";
  const setViewMode = useCallback((mode: "list" | "calendar") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mode === "calendar") next.set("view", "calendar");
      else next.delete("view");
      return next;
    });
  }, [setSearchParams]);

  // Group events by date for calendar view
  const eventsByDate = useMemo(() => {
    const map = new Map<string, DiscoverEvent[]>();
    for (const e of events) {
      const dateKey = e.starts_at.split("T")[0];
      const existing = map.get(dateKey) ?? [];
      existing.push(e);
      map.set(dateKey, existing);
    }
    return map;
  }, [events]);

  return (
    <div data-page="discover" data-state={loading ? "loading" : error ? "error" : "ready"}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Discover Events</h1>
          <p className="text-sm text-muted-foreground">Browse upcoming sporting events across all schools</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search events or schools..."
            value={search}
            onChange={(e) => updateParam("search", e.target.value)}
            className="pl-9"
          />
        </div>

        {filters.sports.length > 0 && (
          <select
            value={sport}
            onChange={(e) => updateParam("sport", e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            title="Filter by sport"
          >
            <option value="">All sports</option>
            {filters.sports.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}

        <select
          value={type}
          onChange={(e) => updateParam("type", e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          title="Filter by event type"
        >
          <option value="">All types</option>
          <option value="match_day">Match Day</option>
          <option value="practice">Practice</option>
          <option value="tryout">Tryout</option>
        </select>

        {filters.provinces.length > 0 && (
          <select
            value={province}
            onChange={(e) => updateParam("province", e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            title="Filter by province"
          >
            <option value="">All provinces</option>
            {filters.provinces.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        )}
      </div>

      {/* View mode tabs */}
      <div className="mb-4 flex gap-1 rounded-lg bg-muted p-1 w-fit">
        <button
          type="button"
          onClick={() => setViewMode("list")}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            viewMode === "list" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <List className="h-4 w-4" />
          List
        </button>
        <button
          type="button"
          onClick={() => setViewMode("calendar")}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            viewMode === "calendar" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Calendar className="h-4 w-4" />
          Calendar
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Loading events...</div>
      ) : error ? (
        <div className="py-12 text-center text-destructive">{error}</div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">No events found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your filters or search</p>
          {/* 102: soft sign-in nudge -- anonymous spectators get a conversion
              prompt in the empty state. Not a gate; users still see public
              discover events when present. 200: signed-in (roleless) users get
              follow/link guidance instead of being told to sign in again. */}
          {user ? (
            <p className="mt-6 text-sm text-muted-foreground" data-region="discover-nudge-signed-in">
              <Link to="/preferences" className="text-primary hover:underline">Follow schools</Link>
              {" "}to personalise this feed, or{" "}
              <Link to="/my-children" className="text-primary hover:underline">link your children</Link>
              {" "}with a family code.
            </p>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground" data-region="discover-nudge-anonymous">
              <Link to="/login" className="text-primary hover:underline">Sign in</Link>
              {" "}for personalised events from the schools you follow.
            </p>
          )}
        </div>
      ) : viewMode === "calendar" ? (
        <DiscoverCalendarView events={events} eventsByDate={eventsByDate} onEventClick={(id) => navigate(`/events/${id}`)} />
      ) : (
        <>
          <div className="mb-2 text-sm text-muted-foreground">
            {total} event{total !== 1 ? "s" : ""} found
          </div>
          {/* 203 -- one columns array drives the sm+ table and the <sm cards
              (spectators browse discover on phones; the 5-column table
              horizontally scrolled at 390px). */}
          <div className="rounded-md border max-sm:rounded-none max-sm:border-0">
            <ResponsiveTable<DiscoverEvent>
              columns={[
                {
                  key: "event",
                  header: "Event",
                  cardRole: "title",
                  render: (event) => (
                    <>
                      <div className="font-medium">{event.name}</div>
                      {event.sport_name && (
                        <div className="text-xs text-muted-foreground">{event.sport_name}</div>
                      )}
                    </>
                  ),
                },
                {
                  key: "school",
                  header: "School",
                  render: (event) => (
                    <div className="flex items-center gap-2">
                      <SchoolCrest name={event.school_name} logoUrl={event.school_logo_url} primaryColor={event.school_primary_color} size={24} />
                      <div>
                        <div>{event.school_name}</div>
                        {event.school_location && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {event.school_location}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "type",
                  header: "Type",
                  render: (event) => (
                    <span className={typeBadge[event.event_type] ?? "text-xs"}>
                      {event.event_type === "match_day" ? "Match Day" : event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                    </span>
                  ),
                },
                {
                  key: "date",
                  header: "Date",
                  render: (event) => (
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDate(event.starts_at)}
                    </div>
                  ),
                },
                { key: "matches", header: "Matches", headerClassName: "text-right", className: "text-right", render: (event) => String(event.match_count) },
              ]}
              rows={events}
              rowKey={(event) => event.id}
              rowProps={(event) => ({ "data-item": "event", "data-item-id": event.id })}
              rowClassName={() => "cursor-pointer hover:bg-muted/50"}
              onRowClick={(event) => navigate(`/events/${event.id}`)}
            />
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Calendar View ---

type CalendarMode = "year" | "quarter" | "month" | "week" | "day";
const CAL_MODES: { value: CalendarMode; label: string }[] = [
  { value: "year", label: "Year" },
  { value: "quarter", label: "Quarter" },
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function toDateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function eventDotColor(type: string) {
  return type === "match_day" ? "bg-blue-500" : type === "practice" ? "bg-green-500" : "bg-orange-500";
}

function DiscoverCalendarView({
  events,
  eventsByDate,
  onEventClick,
}: {
  events: DiscoverEvent[];
  eventsByDate: Map<string, DiscoverEvent[]>;
  onEventClick: (id: string) => void;
}) {
  const [mode, setMode] = useState<CalendarMode>("month");
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const todayKey = toDateKey(new Date());

  // Navigation
  function navigate(dir: -1 | 1) {
    setCursor((prev) => {
      const d = new Date(prev);
      if (mode === "year") d.setFullYear(d.getFullYear() + dir);
      else if (mode === "quarter") d.setMonth(d.getMonth() + dir * 3);
      else if (mode === "month") d.setMonth(d.getMonth() + dir);
      else if (mode === "week") d.setDate(d.getDate() + dir * 7);
      else d.setDate(d.getDate() + dir);
      return d;
    });
    setSelectedDate(null);
  }

  function navLabel(): string {
    if (mode === "year") return String(cursor.getFullYear());
    if (mode === "quarter") {
      const q = Math.floor(cursor.getMonth() / 3) + 1;
      return `Q${q} ${cursor.getFullYear()}`;
    }
    if (mode === "month") return cursor.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
    if (mode === "week") {
      const start = getWeekStart(cursor);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }

  const selectedEvents = selectedDate ? (eventsByDate.get(selectedDate) ?? []) : [];

  return (
    <div>
      <div className="mb-2 text-sm text-muted-foreground">
        {events.length} event{events.length !== 1 ? "s" : ""} in range
      </div>

      {/* Mode selector + navigation */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-1 rounded-lg bg-muted p-0.5">
          {CAL_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => { setMode(m.value); setSelectedDate(null); }}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === m.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[180px] text-center text-sm font-semibold">{navLabel()}</span>
          <Button variant="ghost" size="sm" onClick={() => navigate(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => { setCursor(new Date()); setSelectedDate(null); }} className="text-xs">Today</Button>
        </div>
      </div>

      {/* View body */}
      {mode === "year" && <YearView year={cursor.getFullYear()} eventsByDate={eventsByDate} todayKey={todayKey} onDayClick={(dk) => { setSelectedDate(dk); setMode("day"); setCursor(new Date(dk + "T00:00:00")); }} />}
      {mode === "quarter" && <QuarterView cursor={cursor} eventsByDate={eventsByDate} todayKey={todayKey} onDayClick={(dk) => { setSelectedDate(dk); setMode("day"); setCursor(new Date(dk + "T00:00:00")); }} />}
      {mode === "month" && <MonthGrid year={cursor.getFullYear()} month={cursor.getMonth()} eventsByDate={eventsByDate} todayKey={todayKey} selectedDate={selectedDate} onDayClick={(dk) => setSelectedDate(selectedDate === dk ? null : dk)} large />}
      {mode === "week" && <WeekView cursor={cursor} eventsByDate={eventsByDate} todayKey={todayKey} onEventClick={onEventClick} />}
      {mode === "day" && <DayView dateKey={selectedDate ?? toDateKey(cursor)} eventsByDate={eventsByDate} onEventClick={onEventClick} />}

      {/* Selected day detail (month mode only) */}
      {mode === "month" && selectedDate && <EventList events={selectedEvents} dateLabel={new Date(selectedDate + "T00:00:00").toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })} onEventClick={onEventClick} />}
    </div>
  );
}

// --- Sub-views ---

function MonthGrid({ year, month, eventsByDate, todayKey, selectedDate, onDayClick, large }: {
  year: number; month: number; eventsByDate: Map<string, DiscoverEvent[]>; todayKey: string;
  selectedDate?: string | null; onDayClick: (dk: string) => void; large?: boolean;
}) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const cellH = large ? "h-20" : "h-8";

  return (
    <div>
      {large && (
        <div className="grid grid-cols-7 gap-px text-center text-xs font-medium text-muted-foreground mb-1">
          {DAYS.map((d) => <div key={d} className="py-1">{d}</div>)}
        </div>
      )}
      <div className="grid grid-cols-7 gap-px">
        {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} className={cellH} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dk = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayEvents = eventsByDate.get(dk) ?? [];
          const isToday = dk === todayKey;
          const isSel = dk === selectedDate;

          return (
            <button type="button" key={dk} onClick={() => onDayClick(dk)}
              className={`${cellH} rounded-md ${large ? "border p-1" : "p-0.5"} text-left transition-colors ${
                isSel ? "border-primary bg-primary/5" : dayEvents.length > 0 ? (large ? "border-border hover:border-primary/50" : "") + " hover:bg-muted/50" : large ? "border-transparent" : "" } hover:bg-muted/30`}>
              <span className={`text-xs ${large ? "font-medium" : ""} ${isToday ? "rounded-full bg-primary px-1 py-0.5 text-white" : ""}`}>{day}</span>
              {large && dayEvents.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-0.5">
                  {dayEvents.slice(0, 3).map((e) => <div key={e.id} className={`h-1.5 w-1.5 rounded-full ${eventDotColor(e.event_type)}`} title={e.name} />)}
                  {dayEvents.length > 3 && <span className="text-[9px] text-muted-foreground">+{dayEvents.length - 3}</span>}
                </div>
              )}
              {!large && dayEvents.length > 0 && (
                <div className="flex justify-center gap-0.5 mt-0.5">
                  {dayEvents.slice(0, 2).map((e) => <div key={e.id} className={`h-1 w-1 rounded-full ${eventDotColor(e.event_type)}`} />)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ year, eventsByDate, todayKey, onDayClick }: {
  year: number; eventsByDate: Map<string, DiscoverEvent[]>; todayKey: string; onDayClick: (dk: string) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-6 md:grid-cols-4">
      {Array.from({ length: 12 }).map((_, m) => (
        <div key={m}>
          <div className="mb-1 text-center text-xs font-semibold text-muted-foreground">{MONTH_NAMES[m]}</div>
          <div className="grid grid-cols-7 gap-px text-center text-[9px] text-muted-foreground mb-0.5">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <MonthGrid year={year} month={m} eventsByDate={eventsByDate} todayKey={todayKey} onDayClick={onDayClick} />
        </div>
      ))}
    </div>
  );
}

function QuarterView({ cursor, eventsByDate, todayKey, onDayClick }: {
  cursor: Date; eventsByDate: Map<string, DiscoverEvent[]>; todayKey: string; onDayClick: (dk: string) => void;
}) {
  const qStart = Math.floor(cursor.getMonth() / 3) * 3;
  return (
    <div className="grid grid-cols-3 gap-6">
      {[0, 1, 2].map((offset) => {
        const m = qStart + offset;
        return (
          <div key={m}>
            <div className="mb-1 text-center text-xs font-semibold">{MONTH_NAMES[m]} {cursor.getFullYear()}</div>
            <div className="grid grid-cols-7 gap-px text-center text-[10px] text-muted-foreground mb-0.5">
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <MonthGrid year={cursor.getFullYear()} month={m} eventsByDate={eventsByDate} todayKey={todayKey} onDayClick={onDayClick} />
          </div>
        );
      })}
    </div>
  );
}

function getWeekStart(d: Date) {
  const date = new Date(d);
  const dow = (date.getDay() + 6) % 7; // Monday=0
  date.setDate(date.getDate() - dow);
  return date;
}

function WeekView({ cursor, eventsByDate, todayKey, onEventClick }: {
  cursor: Date; eventsByDate: Map<string, DiscoverEvent[]>; todayKey: string; onEventClick: (id: string) => void;
}) {
  const start = getWeekStart(cursor);
  return (
    <div className="grid grid-cols-7 gap-2">
      {Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        const dk = toDateKey(d);
        const dayEvents = eventsByDate.get(dk) ?? [];
        const isToday = dk === todayKey;
        return (
          <div key={dk} className={`rounded-md border p-2 min-h-[calc(100vh-280px)] flex flex-col ${isToday ? "border-primary bg-primary/5" : "border-border"}`}>
            <div className={`text-xs font-medium mb-2 pb-1 border-b ${isToday ? "text-primary border-primary/20" : "text-muted-foreground border-border"}`}>
              {DAYS[i]} <span className={`text-sm ${isToday ? "font-bold" : ""}`}>{d.getDate()}</span>
            </div>
            <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
              {dayEvents.map((e) => {
                const time = formatDate(e.starts_at).split(",").pop()?.trim() ?? "";
                return (
                  <button key={e.id} type="button" onClick={() => onEventClick(e.id)}
                    className={`rounded px-2 py-1.5 text-left text-xs leading-snug transition-colors hover:opacity-80 ${
                      e.event_type === "match_day" ? "bg-blue-50 border-l-2 border-blue-500 text-blue-900" : e.event_type === "practice" ? "bg-green-50 border-l-2 border-green-500 text-green-900" : "bg-orange-50 border-l-2 border-orange-500 text-orange-900"
                    }`}>
                    <div className="text-[10px] opacity-60 mb-0.5">{time}</div>
                    <div className="font-medium truncate">{e.name}</div>
                    <div className="truncate opacity-60 text-[11px]">{e.school_name}</div>
                    {e.match_count > 0 && <div className="text-[10px] opacity-50 mt-0.5">{e.match_count} match{e.match_count !== 1 ? "es" : ""}</div>}
                  </button>
                );
              })}
              {dayEvents.length === 0 && <div className="flex-1 flex items-center justify-center text-[10px] text-muted-foreground/40">--</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ dateKey, eventsByDate, onEventClick }: {
  dateKey: string; eventsByDate: Map<string, DiscoverEvent[]>; onEventClick: (id: string) => void;
}) {
  const dayEvents = eventsByDate.get(dateKey) ?? [];
  const label = new Date(dateKey + "T00:00:00").toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">{label}</h3>
      {dayEvents.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No events on this day</p>
      ) : (
        <EventList events={dayEvents} onEventClick={onEventClick} />
      )}
    </div>
  );
}

function EventList({ events: evts, dateLabel, onEventClick }: {
  events: DiscoverEvent[]; dateLabel?: string; onEventClick: (id: string) => void;
}) {
  return (
    <div className="mt-4 rounded-md border p-4">
      {dateLabel && <h3 className="mb-3 text-sm font-semibold">{dateLabel}</h3>}
      {evts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No events</p>
      ) : (
        <div className="flex flex-col gap-2">
          {evts.map((event) => (
            <button type="button" key={event.id} onClick={() => onEventClick(event.id)}
              className="flex items-center justify-between rounded-md border p-3 text-left hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <SchoolCrest name={event.school_name} logoUrl={event.school_logo_url} primaryColor={event.school_primary_color} size={28} />
                <div className="min-w-0">
                  <div className="font-medium text-sm">{event.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{event.school_name}{event.school_location ? ` \u2022 ${event.school_location}` : ""}</div>
                </div>
              </div>
              <div className="text-right">
                <span className={typeBadge[event.event_type] ?? "text-xs"}>
                  {event.event_type === "match_day" ? "Match Day" : event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                </span>
                <div className="text-xs text-muted-foreground mt-0.5">{formatDate(event.starts_at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
